import { useEffect } from "react";
import classNames from "classnames";
import { omit, pick } from "lodash";
import { useLessons } from "@/react/components/Spaces/CourseSpace/Lessons/Edit/hooks/useLessons";
import { useCurrentLessonContext } from "@/react/contexts/currentLessonContext";
import { LoaderRow } from "@circle-react/components/shared/LoaderRow";
import { Layout } from "@circle-react-shared/CustomizeUI";
import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/ModalV2";
import { ContentEditor } from "./Content";
import { EditLessonHeader } from "./Header";
import { Sidebar } from "./Sidebar";

const transformQuiz = quiz => ({
  ...omit(quiz, "questions"),
  questions_attributes: quiz.questions.map(question => ({
    ...omit(question, "options", "cover_media"),
    cover_media: question.cover_media?.signed_id || null,
    options_attributes: question.options,
  })),
});

export const EditLesson = () => {
  const {
    currentLesson: lesson,
    isLoading,
    updateLesson,
    isUpdatingLesson,
  } = useLessons({ forEdition: true });

  const { isSubmitEnabled, setCurrentLesson } = useCurrentLessonContext();

  useEffect(() => {
    setCurrentLesson(lesson);
  }, [lesson, setCurrentLesson]);

  const handleOnSubmit = formData => {
    let featuredMediaPayload;
    if (formData.featured_media) {
      featuredMediaPayload = formData.featured_media.signed_id;
    } else {
      // Send null payload if the lesson had a featured_media already, to remove it.
      if (lesson.featured_media) {
        featuredMediaPayload = null;
      }
      // Else, do not send anything so that the backend will keep the current featured_media.
    }
    const payload = {
      ...pick(
        formData,
        "name",
        "status",
        "rich_text_body",
        "download_featured_media",
        "featured_media_enabled",
        "enforce_featured_media_completion",
        "autocomplete_on_featured_media_completion",
        "comments_enabled",
        "default_tab",
      ),
      featured_media: featuredMediaPayload,
      featured_media_embed_id: formData.featured_media_embed?.id || null,
      ...(formData.quiz && {
        quizzes_attributes: [transformQuiz(formData.quiz)],
      }),
    };

    updateLesson(payload);
  };

  return (
    <Modal
      isOpen
      className="bg-primary h-screen w-screen"
      contentPortalId={`lessons-edit-${lesson?.id}`}
    >
      <Modal.Content size="screen">
        <Layout variant="no-sidebar">
          <EditLessonHeader
            isDisabled={isLoading || !isSubmitEnabled}
            isSaving={isUpdatingLesson}
          />
          {isLoading || !lesson ? (
            <div
              className={classNames(
                "grid-in-main flex h-full w-full items-center justify-center",
              )}
            >
              <LoaderRow />
            </div>
          ) : (
            <Form
              // Remount the form when lesson is updated or if the user navigated to another lesson.
              id="edit-lesson-form"
              key={lesson.updated_at + lesson.id}
              onSubmit={handleOnSubmit}
              preventAccidentalLeave
              defaultValues={{
                featured_media_enabled: lesson.featured_media_enabled,
                featured_media: lesson.featured_media,
                featured_media_embed: lesson.featured_media_embed,
                download_featured_media: lesson.download_featured_media,
                enforce_featured_media_completion:
                  lesson.enforce_featured_media_completion,
                autocomplete_on_featured_media_completion:
                  lesson.autocomplete_on_featured_media_completion,
                comments_enabled: lesson.comments_enabled,
                default_tab: lesson.default_tab,
                custom_thumbnail: null,
                comments: "visible",
                free_preview_enabled: false,
                content: null,
                status: lesson.status,
                name: lesson.name,
                "rich_text_body.body": lesson.rich_text_body.body,
                ...(lesson.quiz && {
                  set_a_passing_grade: lesson.quiz.passing_grade !== null,
                  quiz: lesson.quiz,
                }),
              }}
              className="grid-in-main grid-areas-customize-ui h-full w-full gap-4 overflow-auto"
              formClassName="h-full w-full flex"
            >
              <Sidebar lesson={lesson} />
              <ContentEditor lesson={lesson} />
            </Form>
          )}
        </Layout>
      </Modal.Content>
    </Modal>
  );
};
